import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import type { DocumentView, Location, Scanner } from '../../api/types';
import { useSession } from '../../auth/session';
import {
  Button,
  DataTable,
  ErrorState,
  FilterBar,
  FilterChip,
  LiveBadge,
  Select,
  Skeleton,
  StatusBadge,
  Tooltip,
  useToast,
  type Column,
  type FilterOption,
} from '../../components';
import { categoryColor } from '../../lib/category';

import { useDocumentsApi } from './client';
import './documents.css';
import { UploadDialog } from './UploadDialog';
import {
  clearedFilters,
  hasActiveFilters,
  parseFilters,
  serializeFilters,
  STATUS_KEYS,
  toExportParams,
  withDimension,
  withPage,
  withPerPage,
  withQuery,
  withStatus,
  PAGE_SIZES,
  type DocFilters,
  type StatusKey,
} from './filters';
import {
  categoryOf,
  confidenceOf,
  confidenceText,
  dateTimeLabel,
  pagesLabel,
  sourceLabel,
  toDocStatus,
} from './format';
import { useDocuments, type DocumentsData } from './useDocuments';

const STATUS_LABELS: Record<StatusKey, string> = {
  all: 'All',
  classified: 'Classified',
  needs_review: 'Needs review',
  processing: 'Processing',
  failed: 'Failed',
};

const SOURCE_OPTIONS: FilterOption[] = [
  { value: 'api', label: 'API' },
  { value: 'mcp', label: 'MCP' },
  { value: 'ui', label: 'UI' },
  { value: 'webhook', label: 'Webhook' },
];

/**
 * The explorer's column tracks: filename, type·conf, location, scanner, source,
 * pages, ingested, status.
 *
 * Two deliberate choices, both fixing the same symptom — a horizontal scrollbar
 * and clipped text:
 *
 * 1. **`minmax(0, Nfr)` for the text columns**, not a bare `Nfr`. A bare `fr`
 *    track floors at its content's min-content width, so a long unbroken type
 *    name ("Compliance / Regulatory Document") pushed the grid wider than the
 *    card and the table scrolled sideways. `minmax(0, …)` lets the track go
 *    narrower than its content and the cell ellipsise instead.
 * 2. **Fixed widths for the columns whose content has a known ceiling** — a
 *    channel name, a page count, a timestamp, a status pill. Giving those `fr`
 *    handed spare width to columns that could never use it while the type name,
 *    which always wants more, was rationed.
 */
const GRID =
  'minmax(0, 1.3fr) minmax(0, 2.6fr) minmax(0, 0.9fr) minmax(0, 1.5fr) 62px 52px 96px 100px';

/**
 * The URL flag that opens the manual-upload dialog (the query half of
 * {@link UPLOAD_PATH}). It lives in the query string rather than in component
 * state so the Dashboard's first-run empty state — and any other screen — can
 * send someone straight into the upload flow with a link. It is not a filter:
 * {@link serializeFilters} never emits it, so the first filter change clears it.
 */
const UPLOAD_PARAM = 'upload';

function locationMap(locations: Location[]): Map<string, string> {
  return new Map(locations.map((l) => [l.id, l.name]));
}
function scannerMap(scanners: Scanner[]): Map<string, string> {
  return new Map(scanners.map((s) => [s.id, s.device_id]));
}

export function DocumentsScreen(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { hasPermission } = useSession();
  const canUpload = hasPermission('documents:write');
  const canResolve = hasPermission('review:resolve');
  const canExport = hasPermission('analytics:export');
  const docsApi = useDocumentsApi();

  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);
  const state = useDocuments(filters);

  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState(filters.q ?? '');
  // The rows-per-page control lives in the toolbar beside the search box, so its
  // label association is owned here rather than by the footer.
  const perPageId = useId();

  // Keep the search box in sync when the URL query changes elsewhere (clear-all).
  useEffect(() => {
    setSearch(filters.q ?? '');
  }, [filters.q]);

  const setFilters = useCallback(
    (next: DocFilters) => {
      setSelected([]);
      setSearchParams(serializeFilters(next));
    },
    [setSearchParams],
  );

  const go = useCallback(
    (to: string) => {
      void navigate(to);
    },
    [navigate],
  );

  const openDoc = useCallback(
    (doc: DocumentView) => {
      const suffix = searchParams.toString();
      go(`/documents/${doc.id}${suffix === '' ? '' : `?${suffix}`}`);
    },
    [go, searchParams],
  );

  const uploadOpen = searchParams.get(UPLOAD_PARAM) === '1';

  const openUpload = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.set(UPLOAD_PARAM, '1');
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  // Closing replaces the history entry, so Back leaves Documents rather than
  // re-opening the dialog the user just dismissed.
  const closeUpload = useCallback(
    (accepted: number) => {
      const next = new URLSearchParams(searchParams);
      next.delete(UPLOAD_PARAM);
      setSearchParams(next, { replace: true });
      if (accepted > 0) {
        toast(
          `${String(accepted)} document${accepted === 1 ? '' : 's'} submitted — watch the status change as they process`,
        );
        if (state.status === 'ready') state.reload();
      }
    },
    [searchParams, setSearchParams, toast, state],
  );

  const saveView = useCallback(() => {
    const nav = navigator as { clipboard?: Clipboard };
    if (nav.clipboard) void nav.clipboard.writeText(window.location.href).catch(() => undefined);
    toast('View saved — find it under Documents → Saved views');
  }, [toast]);

  const runBulk = useCallback(
    (kind: 'review' | 'reclassify') => {
      const ids = [...selected];
      if (ids.length === 0) return;
      const reload = state.status === 'ready' ? state.reload : undefined;
      void (async () => {
        try {
          await Promise.all(
            ids.map((id) => (kind === 'review' ? docsApi.sendToReview(id) : docsApi.reclassify(id))),
          );
          toast(
            kind === 'review'
              ? `${String(ids.length)} documents sent to review`
              : `${String(ids.length)} documents queued for re-classification (q.reclassify)`,
          );
          setSelected([]);
          reload?.();
        } catch {
          toast('That action could not be completed — please retry.', { tone: 'error' });
        }
      })();
    },
    [selected, docsApi, toast, state],
  );

  const [exporting, setExporting] = useState(false);
  const runExport = useCallback(() => {
    setExporting(true);
    void docsApi
      .export(toExportParams(filters))
      .then(() => {
        toast('Export started — CSV will be emailed to you');
      })
      .catch(() => {
        toast('Could not start the export — please retry.', { tone: 'error' });
      })
      .finally(() => {
        setExporting(false);
      });
  }, [docsApi, filters, toast]);

  // The load state selects the BODY only. The upload dialog is deliberately a
  // fixed sibling of it rather than something each branch renders for itself:
  // React would otherwise unmount and remount the dialog when the list finishes
  // loading, throwing away a queue of files somebody had already chosen. It also
  // means arriving on `?upload=1` reaches the upload flow while the list is
  // still loading, or has failed to load — which is exactly when you want it.
  const body = renderBody();

  return (
    <section
      // Locked only while the table is up: the frame fills the viewport and hands
      // scrolling to the row list, so the filters and the pager stay put. The
      // loading and error states are short and centred, and clipping them to the
      // viewport could put a Retry button out of reach on a small screen.
      className={state.status === 'ready' ? 'ds-docs ds-docs--locked' : 'ds-docs'}
      aria-label="Documents"
      {...(state.status === 'loading' ? { 'aria-busy': true } : {})}
    >
      {body}
      <UploadDialog open={uploadOpen} onClose={closeUpload} />
    </section>
  );

  function renderBody(): React.JSX.Element {
    if (state.status === 'loading') return <DocumentsLoading />;
    if (state.status === 'error') {
      return (
        <ErrorState
          title="We couldn't load your documents"
          body="The documents service didn't respond. Your documents are safe — this only affects this list."
          traceId={state.traceId ?? undefined}
          actions={
            <Button variant="primary" onClick={state.reload}>
              Retry
            </Button>
          }
        />
      );
    }

    const { data } = state;
  const locNames = locationMap(data.locations);
  const scNames = scannerMap(data.scanners);
  const active = hasActiveFilters(filters);

  const columns: Column<DocumentView>[] = [
    {
      id: 'fn',
      header: 'Filename',
      sortValue: (d) => d.filename.toLowerCase(),
      render: (d) => (
        // No tabIndex here: this column is also the row-open button (DataTable
        // wraps index-0 in a <button> when onRowClick is set), which is
        // already the cell's tab stop — a second one just for the tooltip
        // would be a redundant, confusing extra stop right behind it.
        <Tooltip content={d.filename} visualOnly>
          <span className="ds-docs__fn">{d.filename}</span>
        </Tooltip>
      ),
    },
    {
      id: 'cat',
      header: 'Type · conf',
      sortValue: (d) => categoryOf(d),
      render: (d) => {
        const name = categoryOf(d);
        return (
          <span className="ds-docs__cat">
            {name !== '—' ? (
              <span className="ds-docs__swatch" aria-hidden="true" style={{ background: categoryColor(name) }} />
            ) : null}
            <span className="ds-docs__catname">{name}</span>
            <span className="ds-docs__conf">{confidenceText(confidenceOf(d))}</span>
          </span>
        );
      },
    },
    {
      id: 'loc',
      header: 'Location',
      sortValue: (d) => resolveLocation(d, locNames),
      render: (d) => {
        const value = resolveLocation(d, locNames);
        return (
          <Tooltip content={value} visualOnly>
            <button type="button" className="ds-texttrigger ds-docs__muted">
              {value}
            </button>
          </Tooltip>
        );
      },
    },
    {
      id: 'sc',
      header: 'Scanner',
      sortValue: (d) => resolveScanner(d, scNames),
      render: (d) => {
        const value = resolveScanner(d, scNames);
        return (
          <Tooltip content={value} visualOnly>
            <button type="button" className="ds-texttrigger ds-docs__mono">
              {value}
            </button>
          </Tooltip>
        );
      },
    },
    {
      id: 'src',
      header: 'Source',
      sortValue: (d) => d.capture_metadata.source.channel,
      render: (d) => <span className="ds-docs__muted">{sourceLabel(d.capture_metadata.source.channel)}</span>,
    },
    {
      id: 'pages',
      header: 'Pages',
      sortValue: (d) => d.page_count ?? -1,
      render: (d) => <span className="ds-docs__num">{pagesLabel(d.page_count)}</span>,
    },
    {
      id: 'dt',
      header: 'Ingested',
      sortValue: (d) => d.ingested_at,
      render: (d) => <span className="ds-docs__date">{dateTimeLabel(d.ingested_at)}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      sortValue: (d) => toDocStatus(d.status),
      render: (d) => <StatusBadge status={toDocStatus(d.status)} />,
    },
  ];

    return (
      <>
      <FilterBar
        onClearAll={active ? () => { setFilters(clearedFilters(filters)); } : undefined}
        context={
          <span>
            Filters deep-link ·{' '}
            <button type="button" className="ds-linkbtn" onClick={saveView}>
              Save view
            </button>
          </span>
        }
      >
        <FilterChip
          name="Type"
          options={data.categories.map((c) => ({ value: c.name, label: c.name, count: c.document_count }))}
          selected={filters.type !== undefined ? [filters.type] : []}
          onChange={(sel) => { setFilters(withDimension(filters, 'type', sel[0])); }}
        />
        <FilterChip
          name="Location"
          options={data.locations.map((l) => ({ value: l.id, label: l.name }))}
          selected={filters.location !== undefined ? [filters.location] : []}
          onChange={(sel) => { setFilters(withDimension(filters, 'location', sel[0])); }}
        />
        <FilterChip
          name="Scanner"
          options={data.scanners.map((s) => ({ value: s.id, label: s.device_id }))}
          selected={filters.scanner !== undefined ? [filters.scanner] : []}
          onChange={(sel) => { setFilters(withDimension(filters, 'scanner', sel[0])); }}
        />
        <FilterChip
          name="Source"
          options={SOURCE_OPTIONS}
          selected={filters.source !== undefined ? [filters.source] : []}
          onChange={(sel) => { setFilters(withDimension(filters, 'source', sel[0])); }}
        />
      </FilterBar>

      <div className="ds-docs__toolbar">
        <form
          className="ds-docs__search"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            setFilters(withQuery(filters, search));
          }}
        >
          <span aria-hidden="true">⌕</span>
          <input
            type="search"
            aria-label="Search filenames"
            placeholder="Search filenames…"
            value={search}
            onChange={(e) => { setSearch(e.currentTarget.value); }}
          />
        </form>

        <span className="ds-docs__perpage">
          <label htmlFor={perPageId}>Rows</label>
          <Select
            id={perPageId}
            options={PAGE_SIZES.map((size) => ({ value: String(size), label: String(size) }))}
            value={String(filters.perPage)}
            onChange={(event) => {
              setFilters(withPerPage(filters, Number(event.currentTarget.value)));
            }}
          />
        </span>

        <div className="ds-docs__statuschips" role="group" aria-label="Filter by status">
          {STATUS_KEYS.map((key) => {
            const on = filters.status === key;
            const count = data.counts[key];
            return (
              <button
                key={key}
                type="button"
                className={on ? 'ds-docs__statuschip on' : 'ds-docs__statuschip'}
                aria-pressed={on}
                onClick={() => { setFilters(withStatus(filters, key)); }}
              >
                {STATUS_LABELS[key]}
                {count !== undefined ? (
                  <span className="ds-docs__statuschip-count">{count.toLocaleString('en-GB')}</span>
                ) : null}
              </button>
            );
          })}
        </div>

        <div className="ds-docs__actions">
          {state.processing > 0 ? <LiveBadge count={state.processing} /> : null}
          {canUpload ? (
            <button type="button" className="ds-docs__upload" onClick={openUpload}>
              Upload
            </button>
          ) : null}
          {canExport ? (
            <button
              type="button"
              className="ds-docs__export"
              onClick={runExport}
              disabled={exporting}
            >
              {exporting ? 'Starting…' : 'Export CSV'}
            </button>
          ) : null}
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={data.page.items}
        getRowId={(d) => d.id}
        label="Documents"
        gridTemplate={GRID}
        stickyHeader
        initialSort={{ columnId: 'dt', dir: -1 }}
        selectable
        selectedIds={selected}
        onSelectionChange={setSelected}
        selectionActions={
          canResolve ? (
            <>
              <button type="button" className="ds-docs__selaction" onClick={() => { runBulk('review'); }}>
                Send to review
              </button>
              <button type="button" className="ds-docs__selaction" onClick={() => { runBulk('reclassify'); }}>
                Re-classify
              </button>
            </>
          ) : undefined
        }
        onRowClick={openDoc}
        empty={
          <DocsEmpty
            active={active}
            canUpload={canUpload}
            onClear={() => { setFilters(clearedFilters(filters)); }}
            onUpload={openUpload}
          />
        }
        footer={
          <DocsFooter
            page={data.page}
            perPage={filters.perPage}
            onPrev={() => { setFilters(withPage(filters, filters.page - 1)); }}
            onNext={() => { setFilters(withPage(filters, filters.page + 1)); }}
          />
        }
      />
      </>
    );
  }
}

function resolveLocation(doc: DocumentView, names: Map<string, string>): string {
  const id = doc.capture_metadata.location_id;
  if (id == null) return '—';
  return names.get(id) ?? id;
}

function resolveScanner(doc: DocumentView, names: Map<string, string>): string {
  const id = doc.capture_metadata.scanner_id;
  if (id == null) return '—';
  return names.get(id) ?? id;
}

function DocsFooter({
  page,
  perPage,
  onPrev,
  onNext,
}: {
  page: DocumentsData['page'];
  perPage: number;
  onPrev: () => void;
  onNext: () => void;
}): React.JSX.Element {
  const from = page.total === 0 ? 0 : page.skip + 1;
  const to = Math.min(page.skip + page.items.length, page.total);
  const hasPrev = page.skip > 0;
  // The chosen size, not the constant: with a 100-row page and 60 documents,
  // scaling against 25 would offer a Next that leads to an empty table.
  const hasNext = page.skip + perPage < page.total;
  return (
    <div className="ds-docs__footer">
      <span>
        Showing{' '}
        <strong style={{ color: 'var(--t2)' }}>
          {from.toLocaleString('en-GB')}–{to.toLocaleString('en-GB')}
        </strong>{' '}
        of {page.total.toLocaleString('en-GB')} documents
      </span>
      <div className="ds-docs__pager">
        <button type="button" className="ds-docs__pagebtn" onClick={onPrev} disabled={!hasPrev}>
          ← Prev
        </button>
        <button type="button" className="ds-docs__pagebtn" onClick={onNext} disabled={!hasNext}>
          Next →
        </button>
      </div>
    </div>
  );
}

function DocsEmpty({
  active,
  canUpload,
  onClear,
  onUpload,
}: {
  active: boolean;
  canUpload: boolean;
  onClear: () => void;
  onUpload: () => void;
}): React.JSX.Element {
  if (!active) {
    return (
      <div className="ds-docs__empty">
        <div className="ds-docs__empty-icon" aria-hidden="true">
          ⌕
        </div>
        <p className="ds-docs__empty-title">No documents yet</p>
        <p className="ds-docs__empty-body">
          Scanned documents appear here once a source is connected and ingest begins — or upload one
          by hand to see the pipeline run.
        </p>
        {canUpload ? (
          <Button variant="primary" size="sm" onClick={onUpload}>
            Upload a document
          </Button>
        ) : null}
      </div>
    );
  }
  return (
    <div className="ds-docs__empty">
      <div className="ds-docs__empty-icon" aria-hidden="true">
        ⌕
      </div>
      <p className="ds-docs__empty-title">No documents match</p>
      <p className="ds-docs__empty-body">Your filters are too tight — try removing one, or</p>
      <Button variant="secondary" size="sm" onClick={onClear}>
        Clear all filters
      </Button>
    </div>
  );
}

function DocumentsLoading(): React.JSX.Element {
  return (
    <>
      <div className="ds-docs__toolbar" style={{ borderRadius: 12, border: '1px solid var(--bd)' }}>
        <Skeleton width={230} height={30} radius={8} />
        <Skeleton width={280} height={22} radius={7} />
      </div>
      <div className="ds-card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} width="100%" height={20} radius={5} />
        ))}
      </div>
      <p style={{ color: 'var(--t3)', fontSize: 12.5, margin: 0 }}>Loading documents…</p>
    </>
  );
}
